import { Injectable } from '@angular/core';
import { PaymentMethods, PaymentMethodsType, PaymentResponse, SaleStatus } from '@cargos/sprintpay-models';
import { ReservationService, StorageService } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable } from 'rxjs';
import { PromoCodesService } from './promo-code.service';

export interface PaymentConfirmationInfo extends PaymentResponse {
    paymentMethodSelected?: PaymentMethodsType;
    records?: number[];
}

@Injectable({
    providedIn: 'root',
})
export class PaymentConfirmationService {
    private paymentInformation: BehaviorSubject<PaymentResponse[] | null> = new BehaviorSubject<
        PaymentResponse[] | null
    >(null);
    private wasCartAboveThresholdSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private storageService: StorageService,
        private promoCodeService: PromoCodesService,
        private reservationService: ReservationService
    ) {
        const cartPayResponse: PaymentResponse[] | null =
            this.storageService.getElement<PaymentResponse[]>('cartPayResponse');
        this.paymentInformation.next(cartPayResponse);

        const storedThreshold = this.storageService.getElement<boolean>('wasCartAboveThreshold');
        if (typeof storedThreshold === 'boolean') {
            this.wasCartAboveThresholdSubject.next(storedThreshold);
        }
    }

    sendNotificationEmail(email: string, notificationType: string): Observable<{ message }> {
        return this.reservationService.sendNotificationEmail(email, notificationType);
    }

    getConfirmationInformation(): Observable<PaymentResponse[] | null> {
        return this.paymentInformation.asObservable();
    }

    setConfirmationInformation(cartPayResponse: PaymentResponse[] | null): void {
        this.promoCodeService.resetPromoCode();
        this.storageService.saveElement('cartPayResponse', cartPayResponse);
        this.paymentInformation.next(cartPayResponse);
    }

    setWasCartAboveThreshold(value: boolean): void {
        this.storageService.saveElement('wasCartAboveThreshold', value);
        this.wasCartAboveThresholdSubject.next(value);
    }

    getWasCartAboveThreshold$(): Observable<boolean> {
        return this.wasCartAboveThresholdSubject.asObservable();
    }

    getWasCartAboveThreshold(): boolean {
        return this.wasCartAboveThresholdSubject.getValue();
    }

    getSuccessfulPayments(): PaymentResponse[] {
        return (
            this.paymentInformation.getValue()?.filter((payment) => payment.saleStatus === SaleStatus.SALE_SUCCESS) ||
            []
        );
    }

    getEmailFromSuccessfulPayments(): string | null {
        const successfulPayments = this.getSuccessfulPayments();
        if (!successfulPayments.length) {
            return null;
        }

        const paypalPayment = successfulPayments.find((payment) => payment.paymentMethod === PaymentMethods.PAYPAL);

        if (paypalPayment) {
            return paypalPayment.additionalFields?.providerData?.paypalEmail ?? null;
        }

        const paymentWithEmail = successfulPayments.find(
            (payment) => payment.notificationEmail && payment.notificationEmail.trim() !== ''
        );

        if (paymentWithEmail?.notificationEmail) {
            return paymentWithEmail.notificationEmail;
        }

        return null;
    }

    getTransactionId(): string {
        const successfulPayments = this.getSuccessfulPayments();
        return successfulPayments.map((paymentElement: PaymentResponse) => paymentElement.transactionId)[0] || '';
    }
}
